import { Component, EventEmitter, Input, Output, ViewChild, OnInit } from "@angular/core";
import { ModalController } from "@ionic/angular";
import { LoadingComponent } from "src/app/shared/loading/loading.component";
import { PickerComponent } from "src/app/shared/picker/picker.component";
import { PickerType } from "src/app/shared/picker/models/picker-type.enum";
import { ViewType } from "src/app/shared/picker/models/picker-view-type.enum";
import moment from "moment";
import { ToastService } from "src/app/services/toast.service";
import { AppState } from "src/store/app/app.state";
import { Store } from "@ngxs/store";
import { ClockingRecord } from "src/app/services/response-models/user.response.model";
import { sentryCaptureException } from "src/app/utility/errors";
import { UsersService } from "src/app/services/users.service";

@Component({
    selector: "app-check-in-out-modal",
    templateUrl: "./check-in-out-modal.component.html",
    styleUrls: ["./check-in-out-modal.component.scss"],
    standalone: false,
})
export class CheckInOutModalComponent implements OnInit {
    @ViewChild("loading") loading: LoadingComponent;
    @ViewChild("picker") picker: PickerComponent;

    @Output() onCheckInOut = new EventEmitter<{ type: string; time: Date | null }>();
    @Input() mapUrl: string;
    @Input() type: string;
    @Input() checkAtSpecificTime: boolean;

    public readonly PickerType = PickerType;
    public readonly ViewType = ViewType;
    public time = moment().format("HH:mm");
    public checkedAt: string = null;
    public futureCheckIn: boolean = false;
    private checkTime: Date = null;
    private isValid = true;
    private clockingRecord: Array<ClockingRecord> = null;

    constructor(
        private modalController: ModalController,
        private store: Store,
        private userService: UsersService,
        public toastService: ToastService
    ) {}

    ngOnInit(): void {
        this.clockingRecord = this.store.selectSnapshot(AppState.getUserClockingRecords);
        if (this.clockingRecord[0]) {
            const checked = moment(this.clockingRecord[0].checked_in);
            this.checkedAt = checked.format("HH:mm");
            this.futureCheckIn = checked.isAfter(moment());
        }
    }

    public closeModal = () => {
        this.modalController.dismiss();
    };

    public checkInOut() {
        if (this.isValid) {
            this.onCheckInOut.emit({ type: this.type, time: this.checkTime });
            this.closeModal();
        }
    }

    public async delete() {
        this.loading?.startLoading();
        await this.userService
            .deleteClockingRecord(this.clockingRecord[0].id)
            .then(() => {
                this.userService.isUserCheckedIn();
                this.loading?.endLoading();
                this.closeModal();
                this.toastService.showToastMessage("PROJECT_DETAILS.CHECK_OUT_MESSAGE");
            })
            .catch((err) => {
                this.loading?.endLoading();
                this.closeModal();
                sentryCaptureException(err);
                this.toastService.showGeneralErrorMessage();
            });
    }

    public openPicker() {
        this.picker.show();
    }

    public onStartTimeChange = async (value) => {
        const newValue = value.replace(/:0$/, ":00");
        this.time = newValue;
        const splitted = newValue.split(":");

        // create date object and adjust it
        const dateObj = new Date();
        dateObj.setHours(splitted[0]);
        dateObj.setMinutes(splitted[1]);
        dateObj.setSeconds(0);
        this.checkTime = dateObj;

        if (dateObj.toString() === "Invalid Date") {
            this.toastService.showToastMessage("PROJECT_DETAILS.CHECK_IN_INVALID_DATE", {
                color: "danger",
            });
            this.isValid = false;
            return;
        }

        const now = moment();
        if (this.type === "checkInUser") {
            if (now.isAfter(moment(this.checkTime))) {
                this.toastService.showToastMessage("PROJECT_DETAILS.CHECK_IN_ERROR", {
                    color: "danger",
                });
                this.isValid = false;
                return;
            }
        } else {
            if (
                this.clockingRecord[0].checked_in &&
                moment(this.checkTime).isBefore(moment(this.clockingRecord[0].checked_in))
            ) {
                this.toastService.showToastMessage("PROJECT_DETAILS.CHECK_OUT_BEFORE_IN_ERROR", {
                    color: "danger",
                });
                this.isValid = false;
                return;
            }

            if (now.isBefore(moment(this.checkTime))) {
                this.toastService.showToastMessage("PROJECT_DETAILS.CHECK_OUT_ERROR", {
                    color: "danger",
                });
                this.isValid = false;
                return;
            }
        }

        this.isValid = true;
    };
}
